import React from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import {
  buildDashboardStatsQuery,
  DASHBOARD_PERIOD_PRESETS,
  formatDashboardRangeLabel,
  getDashboardDateRangeFromPreset,
} from '../../utils/dashboardDateRange';

const DashboardDateRangeFilter = ({ value, onChange, disabled = false }) => {
  const [preset, setPreset] = React.useState(value?.preset || 'all_time');
  const [startDate, setStartDate] = React.useState(value?.startDate || '');
  const [endDate, setEndDate] = React.useState(value?.endDate || '');
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!value) return;
    setPreset(value.preset || 'all_time');
    setStartDate(value.startDate || '');
    setEndDate(value.endDate || '');
  }, [value]);

  const applyRange = React.useCallback(
    (nextPreset, nextStart, nextEnd, { force = false } = {}) => {
      const query = buildDashboardStatsQuery({
        preset: nextPreset,
        startDate: nextStart,
        endDate: nextEnd,
      });

      if (nextPreset !== 'all_time' && query === null && !force) {
        setError('End date must be on or after start date.');
        return false;
      }

      setError('');
      onChange({
        preset: nextPreset,
        startDate: nextPreset === 'all_time' ? '' : nextStart,
        endDate: nextPreset === 'all_time' ? '' : nextEnd,
        query: query || {},
      });
      return true;
    },
    [onChange]
  );

  const handlePresetChange = (nextPreset) => {
    setPreset(nextPreset);
    if (nextPreset === 'all_time') {
      setStartDate('');
      setEndDate('');
      applyRange('all_time', '', '');
      return;
    }

    if (nextPreset === 'custom_range') {
      setError('');
      return;
    }

    const range = getDashboardDateRangeFromPreset(nextPreset);
    setStartDate(range.start_date);
    setEndDate(range.end_date);
    applyRange(nextPreset, range.start_date, range.end_date);
  };

  const handleStartDateChange = (nextStart) => {
    setPreset('custom_range');
    setStartDate(nextStart);
    setError('');
  };

  const handleEndDateChange = (nextEnd) => {
    setPreset('custom_range');
    setEndDate(nextEnd);
    setError('');
  };

  const handleApplyCustom = () => {
    applyRange('custom_range', startDate, endDate);
  };

  const rangeLabel = formatDashboardRangeLabel(
    preset === 'all_time' ? '' : startDate,
    preset === 'all_time' ? '' : endDate
  );

  return (
    <div className="dashboard-date-range-filter">
      <Row className="g-2 align-items-end">
        <Col xs={12} md={4} lg={3}>
          <Form.Group>
            <Form.Label className="small text-muted mb-1">Period</Form.Label>
            <Form.Select
              size="sm"
              value={preset}
              disabled={disabled}
              onChange={(e) => handlePresetChange(e.target.value)}
            >
              {DASHBOARD_PERIOD_PRESETS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs={12} md={3} lg={2}>
          <Form.Group>
            <Form.Label className="small text-muted mb-1">Start date</Form.Label>
            <Form.Control
              type="date"
              size="sm"
              value={startDate}
              disabled={disabled || preset === 'all_time'}
              onChange={(e) => handleStartDateChange(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col xs={12} md={3} lg={2}>
          <Form.Group>
            <Form.Label className="small text-muted mb-1">End date</Form.Label>
            <Form.Control
              type="date"
              size="sm"
              value={endDate}
              disabled={disabled || preset === 'all_time'}
              onChange={(e) => handleEndDateChange(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col xs={12} md={2} lg="auto">
          <Button
            variant="outline-primary"
            size="sm"
            className="w-100"
            disabled={disabled || preset !== 'custom_range'}
            onClick={handleApplyCustom}
          >
            Apply
          </Button>
        </Col>
      </Row>
      {rangeLabel && (
        <p className="text-muted small mb-0 mt-2">
          Showing stats for <span className="fw-medium">{rangeLabel}</span>
        </p>
      )}
      {error && <p className="text-danger small mb-0 mt-2">{error}</p>}
    </div>
  );
};

export default DashboardDateRangeFilter;
